import "./Table.scss"

import { CInput, CInputGroup, CInputGroupText, CRow } from "@coreui/react"
import downArrow from "assets/icons/downArrow.svg"
import classNames from "classnames"
import MediaProvider from "components/common/Context/MediaContext"
import { LoadingSpinnerOverlay } from "components/common/loading"
import { TagSearch } from "components/common/tagSearch"
import { Tooltip } from "components/common/tooltip"
import { DRAWER_ITEMS_PER_PAGE, NO_CARDS_PER_PAGE } from "constants/Constants"
import { isEqual } from "lodash-es"
import PropTypes from "prop-types"
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"
import { FaAngleDown, FaSearch } from "react-icons/fa"
import { TiTimes } from "react-icons/ti"

import CMSCard from "../cards/CMSCards"
import { SORT_ORDERS } from "../drawer/constants"
import { MultipleExport } from "../export"
import { NoData } from "../noData"
import Pagination from "../pagination/Pagination"
import { TableTooltip } from "../tooltip"
import CMSTypography, {
  StandardTypographyTypes
} from "../typography/CMSTypography"
import MultiSelectHeader from "./MultiSelectHeader"

export const getExcelDataFromTableData = (name, headers, data) => {
  return {
    name,
    columns: headers.map((header) => header.text),
    data: data.map((eachData) =>
      headers.map((header) => ({ value: eachData[header.key].text || "" }))
    )
  }
}

export default function Table({
  headers = [],
  data = [],
  disableSearch,
  disableSort,
  disableExport,
  enableTagSearch = true,
  defaultSelectedTags,
  excelFilename,
  noDataComponent = <NoData />,
  sheetName,
  preferredSortOrder = SORT_ORDERS.ASCENDING,
  keyMap = {},
  isWidgetTable = false,
  itemsPerPage = DRAWER_ITEMS_PER_PAGE,
  setDefaultSelectedTags,
  dataTestId,
  disablePagination = false,
  infiniteScroll,
  renderSubmitButton,
  enableSubmitButton = false
}) {
  const [searchText, setSearchText] = useState("")
  const [tagSearchText, setTagSearchText] = useState("")
  const [selectedTags, setSelectedTags] = useState({})
  const [isTagSearchDropdownOpen, setIsTagSearchDropdownOpen] = useState(false)
  const [selectedKeyForTagSearch, setSelectedKeyForTagSearch] = useState()
  const [focus, setFocus] = useState(false)
  const [sortKey, setSortKey] = useState(headers[0]?.key)
  const [sortOrder, setSortOrder] = useState(
    preferredSortOrder || SORT_ORDERS.ASCENDING
  )
  const [expandedCardKeys, setExpandedCardKeys] = useState([])
  const [noOfPages, setNoOfPages] = useState(1)
  const [noOfRecords, setNoOfRecords] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [keyValuePairsWorker, setKeyValuePairs] = useState([])
  const [noOfCards, setNoOfCards] = useState(NO_CARDS_PER_PAGE)
  const [isAddingMoreCards, setIsAddingMoreCards] = useState(false)

  const [tagsLoading, setTagsLoading] = useState(false)
  const [isFetching, setIsFetching] = useState(false)

  const wrapperRef = useRef(null)
  const { isSmallScreen } = useContext(MediaProvider)
  const tableRef = useRef(null)

  // Uing Web-Worker for managing heavy tasks
  const worker = useMemo(() => new Worker("/workers/worker.js"), [])

  // This effect set the selected tags of this table every time we are changing tags through chart of widgets
  useEffect(() => {
    if (isWidgetTable && defaultSelectedTags) {
      setSelectedTags(defaultSelectedTags)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSelectedTags])

  useEffect(() => {
    if (!isWidgetTable && defaultSelectedTags) {
      setSelectedTags(defaultSelectedTags)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (sortOrder === SORT_ORDERS.NONE) setSortKey(null)
  }, [sortOrder])

  const multiSelectFilterData = useMemo(() => {
    const filterableHeaders = headers.filter(
      (header) => header.multiSelectFilter
    )
    return data.filter((eachData) =>
      filterableHeaders.every((eachHeader) =>
        eachHeader.multiselectedKeys.includes(eachData[eachHeader.key].text)
      )
    )
  }, [data, headers])

  // filtering data if search enabled based on selectedTags and searchText
  const filteredData = useMemo(() => {
    if (disableSearch) return multiSelectFilterData
    if (enableTagSearch) {
      const lowerTagSearchText = isSmallScreen
        ? searchText.toLowerCase()
        : tagSearchText.toLowerCase()
      return multiSelectFilterData
        .filter((eachData) =>
          Object.entries(selectedTags).every(([selectedKey, selectedValues]) =>
            selectedValues.some((selectedValue) => {
              return eachData[selectedKey]?.tagSearchItems
                ? eachData[selectedKey]?.tagSearchItems.includes(selectedValue)
                : eachData[selectedKey]?.text === selectedValue
            })
          )
        )
        .filter((eachData) =>
          headers.some((eachHeader) =>
            `${eachData[eachHeader.key].text || ""}`
              .toLowerCase()
              ?.includes(lowerTagSearchText)
          )
        )
    } else {
      if (!searchText) return multiSelectFilterData
      const lowerSearchText = searchText.toLowerCase()
      return multiSelectFilterData.filter((eachData) =>
        headers.some((eachHeader) =>
          `${eachData[eachHeader.key].text || ""}`
            .toLowerCase()
            ?.includes(lowerSearchText)
        )
      )
    }
  }, [
    disableSearch,
    multiSelectFilterData,
    enableTagSearch,
    isSmallScreen,
    searchText,
    tagSearchText,
    selectedTags,
    headers
  ])

  // sorting data based on sortKey and SORT_ORDER
  const sortedData = useMemo(() => {
    if (disableSort || sortOrder === SORT_ORDERS.NONE) return filteredData
    return [...filteredData].sort((aData, bData) => {
      const aColumn = aData?.[sortKey]
      const bColumn = bData?.[sortKey]

      // Handle cases where the column data is missing
      if (!aColumn && !bColumn) return 0
      if (!aColumn) return sortOrder
      if (!bColumn) return -sortOrder
      const aValue =
        aColumn.valueForSorting === undefined
          ? (aColumn.text || "")
          : aColumn.valueForSorting

      const bValue =
        bColumn.valueForSorting === undefined
          ? (bColumn.text || "")
          : bColumn.valueForSorting

      if (typeof aValue === typeof bValue)
        return aValue > bValue ? sortOrder : -sortOrder
      return typeof aValue > typeof bValue ? sortOrder : -sortOrder
    })
  }, [disableSort, filteredData, sortKey, sortOrder])

  useEffect(() => {
    setNoOfRecords(filteredData.length)
    const noOfPage = Math.ceil(filteredData.length / itemsPerPage)
    setNoOfPages(noOfPage)
    if (currentPage > noOfPage && noOfPage > 0) {
      setCurrentPage(1)
    }
  }, [filteredData.length, itemsPerPage])

  const paginatedData = useMemo(() => {
    if (infiniteScroll) return sortedData.slice(0, currentPage * itemsPerPage)
    if (disablePagination) return sortedData
    const indexOfLastItem = currentPage * itemsPerPage
    const indexOfFirstItem = indexOfLastItem - itemsPerPage
    return sortedData?.slice(indexOfFirstItem, indexOfLastItem)
  }, [infiniteScroll, sortedData, currentPage, itemsPerPage, disablePagination])

  const exposedHeaders = useMemo(
    () => headers.filter((header) => !header.hideHeader),
    [headers]
  )

  const sortedExcelDataset = useMemo(
    () =>
      getExcelDataFromTableData(
        sheetName || excelFilename,
        exposedHeaders.filter((header) => !header.disableSearch),
        sortedData
      ),
    [sortedData, excelFilename, exposedHeaders, sheetName]
  )

  const excelDataset = useMemo(
    () =>
      getExcelDataFromTableData(
        sheetName || excelFilename,
        exposedHeaders.filter((header) => !header.disableSearch),
        data
      ),
    [data, excelFilename, exposedHeaders, sheetName]
  )

  // removing _key entry from data
  const removeEle = (data) => {
    const copyData = data.map((e) => {
      const entry = {}
      Object.entries(e).forEach(([key, val]) => {
        if (!key.startsWith("_")) {
          entry[key] = { text: val.text, tagSearchItems: val.tagSearchItems }
        }
      })
      return entry
    })
    return copyData
  }

  // calculating key value pairs for tag search
  const keyValuePairs = useMemo(() => {
    if (!enableTagSearch) return []

    const dataCopy = removeEle(data)
    const multiSelectFilterDataCopy = removeEle(multiSelectFilterData)

    setTagsLoading(true)
    worker.postMessage({
      data: dataCopy,
      multiSelectFilterData: multiSelectFilterDataCopy,
      headers: headers
        .filter((header) => !header.disableSearch)
        .map((header) => ({
          ...header,
          tooltipContent: null,
          ele: null
        })),
      selectedTags
    })

    return keyValuePairsWorker
  }, [
    data,
    enableTagSearch,
    headers,
    keyValuePairsWorker,
    multiSelectFilterData,
    selectedTags,
    worker
  ])

  useEffect(() => {
    worker.onmessage = (event) => {
      if (event && event.data) {
        setTagsLoading(false)
        if (!isEqual(keyValuePairsWorker, event.data)) {
          setKeyValuePairs(event.data)
        }
      }
    }
  }, [keyValuePairsWorker, worker])

  const displayMoreData = useCallback(() => {
    setCurrentPage((currentPage) => currentPage + 1)
    setIsFetching(false)
  }, [])

  const observer = useRef()

  const lastUserRef = useCallback(
    (node) => {
      if (isFetching) return
      if (observer.current) observer.current.disconnect()
      observer.current = new IntersectionObserver((entries) => {
        if (
          entries[0].isIntersecting &&
          sortedData.length / (currentPage * itemsPerPage) > 1
        ) {
          setIsFetching(true)
          setTimeout(() => {
            displayMoreData()
          }, 1000)
        }
      })
      if (node) observer.current.observe(node)
    },
    [isFetching, sortedData.length, currentPage, itemsPerPage, displayMoreData]
  )

  const canCardsBeExpanded = useMemo(
    () => exposedHeaders.some((header) => header.showOnCollapsedCard),
    [exposedHeaders]
  )

  useEffect(() => {
    if (isAddingMoreCards) {
      setTimeout(() => {
        setNoOfCards(noOfCards + NO_CARDS_PER_PAGE)
        setIsAddingMoreCards(false)
      }, 100)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddingMoreCards])

  // handling scroll for small screens to render more cards when last card is reached
  useEffect(() => {
    const handleScroll = () => {
      if (
        isSmallScreen &&
        noOfCards < sortedData.length &&
        wrapperRef.current &&
        wrapperRef.current?.parentElement.clientHeight +
        wrapperRef.current?.parentElement?.scrollTop >=
        wrapperRef.current?.offsetHeight
      ) {
        setIsAddingMoreCards(true)
      }
    }
    window.addEventListener("scroll", handleScroll, true)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [isSmallScreen, noOfCards, sortedData.length])

  // rendering cards instead of table for smaller screens
  if (isSmallScreen)
    return (
      <div ref={wrapperRef}>
        {(!disableSearch || !disableExport) && (
          <div className="d-flex mb-3">
            {!disableSearch && (
              <CInputGroup
                className={classNames(
                  "border cms-search-bar-border-radius bg-white",
                  {
                    "cms-focus": focus
                  }
                )}
              >
                <CInput
                  placeholder="Search"
                  className="cms-search-bar-border-radius cms-no-focus border-0"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onFocus={() => setFocus(true)}
                  onBlur={() => setFocus(false)}
                />
                <CInputGroupText className="cms-search-bar-border-radius bg-white border-0">
                  <TiTimes
                    size="25px"
                    className="cursor-pointer"
                    onClick={() => setSearchText("")}
                  />
                </CInputGroupText>
              </CInputGroup>
            )}
            {!disableExport && (
              <MultipleExport
                fileName={excelFilename}
                excelDatasets={[excelDataset, sortedExcelDataset]}
              />
            )}
          </div>
        )}
        <div className="d-flex flex-column table-row-gap">
          {Array.isArray(sortedData) && sortedData.length > 0
            ? sortedData.slice(0, noOfCards).map((eachData) => (
              <CMSCard
                clickable={Boolean(eachData._onClick)}
                onClick={eachData._onClick}
                key={eachData._key}
              >
                {exposedHeaders
                  .filter(
                    (header) =>
                      !canCardsBeExpanded ||
                      expandedCardKeys.includes(eachData._key) ||
                      header.showOnCollapsedCard
                  )
                  .map((header) => (
                    <div
                      key={header.key}
                      className="d-flex justify-content-between align-items-center w-100 px-1"
                    >
                      <div className="w-50 mbl-table-heading pr-1">
                        {header.ele || header.text}
                      </div>
                      <div
                        className={`w-50 mbl-table-value ${eachData[header.key]._columnOnClick
                          ? "cursor-poiter"
                          : ""
                          }`}
                        onClick={(e) => {
                          if (eachData[header.key]._columnOnClick) {
                            e.stopPropagation()
                            eachData[header.key]._columnOnClick()
                          }
                        }}
                      >
                        {eachData[header.key].ele ||
                          eachData[header.key].text}
                      </div>
                    </div>
                  ))}
                {canCardsBeExpanded && (
                  <div className="w-100 d-flex justify-content-center">
                    <img
                      src={downArrow}
                      alt="down-arrow-icon"
                      className={`${expandedCardKeys.includes(eachData._key)
                        ? "inverted"
                        : ""
                        } collapse-icon`}
                      onClick={(e) => {
                        e.stopPropagation()
                        setExpandedCardKeys(
                          expandedCardKeys.includes(eachData._key)
                            ? expandedCardKeys.filter(
                              (key) => key !== eachData._key
                            )
                            : [...expandedCardKeys, eachData._key]
                        )
                      }}
                    />
                  </div>
                )}
              </CMSCard>
            ))
            : noDataComponent}
        </div>
      </div>
    )
  const requiredWidth = (width) => {
    let result
    if (width?.includes("%")) {
      const widthWithoutPercentage = parseInt(width.slice(0, width.length - 1))
      result = (tableRef?.current?.offsetWidth * widthWithoutPercentage) / 100
      result = result - 16 + "px"
      return { width: result }
    }
    return {
      width
    }
  }

  return (
    <React.Fragment>
      {(!disableSearch || !disableExport) && (
        <div className={classNames("stat-div")} data-tut="cr-table-header">
          {!disableSearch &&
            (enableTagSearch ? (
              <div className="flex-1">
                <TagSearch
                  keyValuePairs={keyValuePairs}
                  keyValuePairsWorker={keyValuePairsWorker}
                  setTagSearchText={setTagSearchText}
                  selectedTags={selectedTags}
                  setSelectedTags={setDefaultSelectedTags || setSelectedTags}
                  keyToDisplayTextMap={headers.reduce(
                    (acc, eachHeader) => ({
                      ...acc,
                      [eachHeader.key]: eachHeader.text
                    }),
                    keyMap || {}
                  )}
                  isDropdownOpen={isTagSearchDropdownOpen}
                  setIsDropdownOpen={setIsTagSearchDropdownOpen}
                  selectedKey={selectedKeyForTagSearch}
                  setSelectedKey={setSelectedKeyForTagSearch}
                  isTagsLoading={tagsLoading}
                />
              </div>
            ) : (
              <div className="d-flex align-items-center w-100">
                <CInputGroup
                  className={classNames(
                    "border cms-search-bar-border-radius bg-white",
                    {
                      "cms-focus": focus
                    }
                  )}
                >
                  <CInput
                    placeholder="Search"
                    className="cms-search-bar-border-radius cms-no-focus border-0"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onFocus={() => setFocus(true)}
                    onBlur={() => setFocus(false)}
                  />
                  <CInputGroupText className="cms-search-bar-border-radius bg-white border-0">
                    <FaSearch size="20px" />
                  </CInputGroupText>
                </CInputGroup>
                <TiTimes
                  size="30px"
                  className="cursor-pointer"
                  onClick={() => setSearchText("")}
                />
              </div>
            ))}
          {!disableExport && (
            <div className="d-flex w-10">
              <MultipleExport
                fileName={excelFilename}
                excelDatasets={[excelDataset, sortedExcelDataset]}
              />
            </div>
          )}
          {enableSubmitButton && renderSubmitButton()}
        </div>
      )}
      <LoadingSpinnerOverlay
        isLoading={isFetching}
        spinnerSize="8rem"
        display="contents"
      >
        <div
          className={classNames({
            "cms-drawer-content": !isWidgetTable,
            "scroll-height": infiniteScroll
          })}
          data-tut="cr-table-data"
        >
          <table
            className={classNames("x-scroll-table", {
              "cms-table": isWidgetTable
            })}
            ref={tableRef}
          >
            <thead>
              <tr className="cms-table-header-row">
                {Array.isArray(exposedHeaders) &&
                  exposedHeaders.map((header, index) => (
                    <th
                      key={header.key}
                      className={
                        index !== exposedHeaders.length - 1
                          ? "cms-table-border-right"
                          : ""
                      }
                    >
                      <div
                        className="cms-table-header-column"
                        style={requiredWidth(header?.width)}
                      >
                        {header.multiSelectFilter ? (
                          <MultiSelectHeader
                            options={header.multiSelectOptions}
                            selectedKeys={header.multiselectedKeys}
                            setSelectedKeys={header.setMultiSelectedKeys}
                            headerText={header.text}
                          />
                        ) : (
                          <CMSTypography
                            type={StandardTypographyTypes.cardSubHeading}
                            onClick={() => {
                              if (enableTagSearch) {
                                setSelectedKeyForTagSearch(header.key)
                                setIsTagSearchDropdownOpen(true)
                              }
                            }}
                            clickable={enableTagSearch}
                          >
                            {header.ele || header.text}
                          </CMSTypography>
                        )}{" "}
                        {(header.tooltipHeading || header.tooltipContent) && (
                          <Tooltip
                            heading={header.tooltipHeading}
                            text={header.tooltipContent}
                            className="ml-1"
                            position={header.headerTooltipPosition}
                          />
                        )}
                        {!disableSort && !header.disableSort && (
                          <div className="d-flex flex-column ml-2">
                            <FaAngleDown
                              className={`cms-sort-arrow-style cms-arrow-inverted ${!(
                                sortKey === header.key &&
                                sortOrder === SORT_ORDERS.ASCENDING
                              )
                                ? "cms-arrow-unselected-color"
                                : ""
                                }`}
                              onClick={() => {
                                setSortKey(header.key)
                                setSortOrder(SORT_ORDERS.ASCENDING)
                              }}
                              data-testid="sort-aero-asc"
                            />
                            <FaAngleDown
                              className={`cms-sort-arrow-style ${!(
                                sortKey === header.key &&
                                sortOrder === SORT_ORDERS.DESCENDING
                              )
                                ? "cms-arrow-unselected-color"
                                : ""
                                }`}
                              onClick={() => {
                                setSortOrder(SORT_ORDERS.DESCENDING)
                                setSortKey(header.key)
                              }}
                              data-testid="sort-aero-desc"
                            />
                          </div>
                        )}
                      </div>
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody className={classNames("cms-table-body")}>
              {Array.isArray(paginatedData) && paginatedData.length > 0 ? (
                paginatedData.map((eachData, index) => (
                  <React.Fragment key={eachData._key}>
                    <tr
                      className={classNames("cms-table-data-row-style", {
                        "cursor-pointer": eachData?._onClick,
                        "alternate-row-bg-color": index % 2 === 1
                      })}
                      key={eachData._key}
                      onClick={() => {
                        if (eachData?._onClick) {
                          eachData._onClick()
                        }
                      }}
                      ref={
                        index === currentPage * itemsPerPage - 1 &&
                          infiniteScroll
                          ? lastUserRef
                          : null
                      }
                      data-testid={`${dataTestId}-${index}`}
                    >
                      {Array.isArray(exposedHeaders) &&
                        exposedHeaders.map((header, index) => (
                          <td
                            key={`${eachData._key}-${header.key}`}
                            className={`
                         ${index !== exposedHeaders.length - 1
                                ? "cms-table-border-right"
                                : ""
                              }
                            ${eachData[header.key]?._columnOnClick
                                ? "cursor-pointer"
                                : ""
                              }
                        `}
                            onClick={() => {
                              if (eachData[header.key]?._columnOnClick) {
                                eachData[header.key]._columnOnClick()
                              }
                            }}
                          >
                            {header.tooltipRequired ? (
                              <TableTooltip
                                position={header.tooltipPosition || "right"}
                                showTooltip
                                tooltipText={eachData[header.key]?.text || ""}
                              >
                                <div
                                  className={`cms-table-data-column-with-tooltip ${header.contentPosition
                                    ? header.contentPosition
                                    : typeof eachData[header.key]
                                      ?.valueForSorting === "number" ||
                                      typeof eachData[header.key].text ===
                                      "number"
                                      ? "justify-content-end d-flex w-100"
                                      : "justify-content-start"
                                    }`}
                                  style={requiredWidth(header?.width)}
                                >
                                  {eachData[header.key]?.ele ||
                                    eachData[header.key]?.text || ""}
                                </div>
                              </TableTooltip>
                            ) : (
                              <div
                                className={`cms-table-data-column-without-tooltip ${header.contentPosition
                                  ? header.contentPosition
                                  : typeof eachData[header.key]
                                    ?.valueForSorting === "number" ||
                                    typeof eachData[header.key]?.text ===
                                    "number"
                                    ? "justify-content-end"
                                    : "justify-content-start"
                                  }`}
                                style={requiredWidth(header?.width)}
                              >
                                {eachData[header.key]?.ele ||
                                  eachData[header.key]?.text || ""}
                              </div>
                            )}
                          </td>
                        ))}
                    </tr>
                    {eachData._expandedRow && (
                      <tr
                        className={classNames({
                          "alternate-row-bg-color": index % 2 === 1,
                          "border-bottom-dashed":
                            index !== paginatedData.length - 1
                        })}
                      >
                        {eachData._expandedRow}
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : (
                <tr>
                  <td colSpan={exposedHeaders.length}>{noDataComponent}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </LoadingSpinnerOverlay>
      {!disablePagination && (
        <div className="d-flex flex-column flex-fill justify-content-end">
          <CRow md="12" className="drawer-footer">
            {noOfPages > 0 ? (
              <Pagination
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                noOfPages={noOfPages}
                noOfRecords={noOfRecords}
              />
            ) : null}
          </CRow>
        </div>
      )}
    </React.Fragment>
  )
}

Table.propTypes = {
  headers: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  disableSearch: PropTypes.bool,
  disableSort: PropTypes.bool,
  disableExport: PropTypes.bool,
  enableTagSearch: PropTypes.bool,
  defaultSelectedTags: PropTypes.object,
  excelFilename: PropTypes.string,
  noDataComponent: PropTypes.node,
  sheetName: PropTypes.string,
  keyMap: PropTypes.object,
  isWidgetTable: PropTypes.bool,
  itemsPerPage: PropTypes.number,
  setDefaultSelectedTags: PropTypes.func,
  dataTestId: PropTypes.string,
  disablePagination: PropTypes.bool,
  infiniteScroll: PropTypes.bool,
  renderSubmitButton: PropTypes.element,
  enableSubmitButton: PropTypes.bool,
  preferredSortOrder: PropTypes.func
}
